import React, { useMemo } from 'react';
import CalcShell from '../../components/CalcShell';
import NumberField from '../../components/NumberField';
import YesNoToggle from '../../components/YesNoToggle';
import RiskScoreResult from '../../components/RiskScoreResult';
import { useUserInput } from '../../context/UserInputContext';
import { CmmlRiskModelId } from '../../types';
import {
  gfmRiskGroups, calculateGfm,
  mayoRiskGroups, calculateMayo,
  cpssMolRiskGroups, calculateCpssMol,
  blastRiskGroups, calculateBlast,
  blastMolRiskGroups, calculateBlastMol,
} from '../../data/diseasesData';

const MODELS: { id: CmmlRiskModelId; label: string }[] = [
  { id: 'gfm', label: 'GFM (2013)' },
  { id: 'mayo', label: 'Mayo Molecular Model (2014)' },
  { id: 'cpss-mol', label: 'CPSS-Mol (2016)' },
  { id: 'blast', label: 'BLAST (2025)' },
  { id: 'blast-mol', label: 'BLAST-mol (2025)' },
];

// ── GFM Calculator ──
const GfmCalc: React.FC = () => {
  const { state, update } = useUserInput();
  const input = state.gfm;
  const setInput = (val: typeof input) => update('gfm', val);

  const result = useMemo(() => {
    const age = parseFloat(input.age);
    const wbc = parseFloat(input.wbc);
    const hb = parseFloat(input.hemoglobin);
    if (isNaN(age) || isNaN(wbc) || isNaN(hb) || !input.sex) return null;
    if (input.plateletsLow === null || input.asxl1 === null) return null;
    return calculateGfm(age, wbc, hb, input.sex, input.plateletsLow, input.asxl1);
  }, [input]);

  return (
    <CalcShell
      title="GFM Prognostic Model"
      subtitle="Groupe Français des Myélodysplasies, 2013"
      inputs={
        <>
          <div className="hema-input-group">
            <label className="hema-label">Sex</label>
            <div className="hema-field">
              <select value={input.sex || ''} onChange={(e) => setInput({ ...input, sex: (e.target.value || null) as typeof input.sex })}>
                <option value="">Select...</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <p className="hema-hint">Anemia threshold: &lt;10 g/dL (F) or &lt;11 g/dL (M)</p>
          </div>
          <NumberField label="Age (years)" value={input.age} onChange={(v) => setInput({ ...input, age: v })} placeholder="e.g. 70" min={0} max={120} />
          <NumberField label={<>WBC (×10³/µL)</>} value={input.wbc} onChange={(v) => setInput({ ...input, wbc: v })} placeholder="e.g. 20" min={0} step={0.1} decimal />
          <NumberField label="Hemoglobin (g/dL)" value={input.hemoglobin} onChange={(v) => setInput({ ...input, hemoglobin: v })} placeholder="e.g. 9" min={0} step={0.1} decimal />
          <YesNoToggle label={<>Platelets &lt;100,000/µL</>} value={input.plateletsLow} onChange={(v) => setInput({ ...input, plateletsLow: v })} />
          <YesNoToggle label="ASXL1 mutation" value={input.asxl1} onChange={(v) => setInput({ ...input, asxl1: v })} />
        </>
      }
      aside={result ? <div className="score-display"><span className="score-label">GFM Score</span><span className="score-value">{result.score}</span></div> : null}
    >
      <RiskScoreResult groups={gfmRiskGroups} activeLevel={result?.level || null} emptyHint="Fill in all fields to calculate GFM score" />
    </CalcShell>
  );
};

// ── Mayo Calculator ──
const MayoCalc: React.FC = () => {
  const { state, update } = useUserInput();
  const input = state.mayo;
  const setInput = (val: typeof input) => update('mayo', val);

  const result = useMemo(() => {
    const hb = parseFloat(input.hemoglobin);
    const amc = parseFloat(input.amc);
    if (isNaN(hb) || isNaN(amc)) return null;
    if (input.plateletsLow === null || input.imcsPresent === null || input.asxl1Frameshift === null) return null;
    return calculateMayo(hb, input.plateletsLow, amc, input.imcsPresent, input.asxl1Frameshift);
  }, [input]);

  return (
    <CalcShell
      title="Mayo Molecular Model"
      subtitle="2014 — includes ASXL1 frameshift/nonsense (1.5 pts)"
      inputs={
        <>
          <NumberField label="Hemoglobin (g/dL)" value={input.hemoglobin} onChange={(v) => setInput({ ...input, hemoglobin: v })} placeholder="e.g. 9" min={0} step={0.1} decimal />
          <YesNoToggle label={<>Platelets &lt;100,000</>} value={input.plateletsLow} onChange={(v) => setInput({ ...input, plateletsLow: v })} />
          <NumberField label={<>AMC (×10⁹/L)</>} value={input.amc} onChange={(v) => setInput({ ...input, amc: v })} placeholder="e.g. 12" min={0} step={0.1} decimal />
          <YesNoToggle label="Peripheral blood IMCs >0%" value={input.imcsPresent} onChange={(v) => setInput({ ...input, imcsPresent: v })} hint="Immature myeloid cells in peripheral blood" />
          <YesNoToggle label="ASXL1 frameshift/nonsense mutation" value={input.asxl1Frameshift} onChange={(v) => setInput({ ...input, asxl1Frameshift: v })} />
        </>
      }
      aside={result ? <div className="score-display"><span className="score-label">Mayo Score</span><span className="score-value">{result.score}</span></div> : null}
    >
      <RiskScoreResult groups={mayoRiskGroups} activeLevel={result?.level || null} emptyHint="Fill in all fields to calculate Mayo score" />
    </CalcShell>
  );
};

// ── CPSS-Mol Calculator ──
const CpssMolCalc: React.FC = () => {
  const { state, update } = useUserInput();
  const input = state.cpssMol;
  const setInput = (val: typeof input) => update('cpssMol', val);

  const result = useMemo(() => {
    const wbc = parseFloat(input.wbc);
    const bmBlasts = parseFloat(input.bmBlasts);
    if (isNaN(wbc) || isNaN(bmBlasts)) return null;
    if (input.runx1 === null || input.asxl1 === null || input.nras === null || input.setbp1 === null) return null;
    if (!input.cpssKaryotype || input.transfusionDependent === null) return null;
    return calculateCpssMol(
      wbc, bmBlasts,
      input.runx1, input.asxl1, input.nras, input.setbp1,
      input.cpssKaryotype as 'normal' | 'intermediate' | 'high',
      input.transfusionDependent
    );
  }, [input]);

  return (
    <CalcShell
      title="CPSS-Mol"
      subtitle="CMML-specific Prognostic Scoring System — Molecular, 2016"
      inputs={
        <>
          <NumberField label={<>WBC (×10³/µL)</>} value={input.wbc} onChange={(v) => setInput({ ...input, wbc: v })} placeholder="e.g. 15" min={0} step={0.1} decimal />
          <NumberField label="BM blasts (%)" value={input.bmBlasts} onChange={(v) => setInput({ ...input, bmBlasts: v })} placeholder="e.g. 6" min={0} max={100} step={0.1} decimal />
          <YesNoToggle label="RUNX1 mutation" value={input.runx1} onChange={(v) => setInput({ ...input, runx1: v })} />
          <YesNoToggle label="ASXL1 mutation" value={input.asxl1} onChange={(v) => setInput({ ...input, asxl1: v })} />
          <YesNoToggle label="NRAS mutation" value={input.nras} onChange={(v) => setInput({ ...input, nras: v })} />
          <YesNoToggle label="SETBP1 mutation" value={input.setbp1} onChange={(v) => setInput({ ...input, setbp1: v })} />
          <div className="hema-input-group">
            <label className="hema-label">CPSS cytogenetic risk</label>
            <div className="hema-field">
              <select value={input.cpssKaryotype} onChange={(e) => setInput({ ...input, cpssKaryotype: e.target.value })}>
                <option value="">Select...</option>
                <option value="normal">Low (normal, isolated -Y)</option>
                <option value="intermediate">Intermediate (other abnormalities)</option>
                <option value="high">High (+8, chromosome 7 abnormalities, complex ≥3)</option>
              </select>
            </div>
          </div>
          <YesNoToggle label="RBC transfusion-dependence" value={input.transfusionDependent} onChange={(v) => setInput({ ...input, transfusionDependent: v })} hint="≥1 transfusion per 8 weeks for ≥4 months" />
        </>
      }
      aside={result ? <div className="score-display"><span className="score-label">CPSS-Mol Score</span><span className="score-value">{result.score}</span></div> : null}
    >
      <RiskScoreResult groups={cpssMolRiskGroups} activeLevel={result?.level || null} emptyHint="Fill in all fields to calculate CPSS-Mol score" />
    </CalcShell>
  );
};

// ── BLAST Calculator ──
const BlastCalc: React.FC = () => {
  const { state, update } = useUserInput();
  const input = state.blast;
  const setInput = (val: typeof input) => update('blast', val);

  const result = useMemo(() => {
    if (!input.sex) return null;
    const hb = parseFloat(input.hemoglobin);
    const blasts = parseFloat(input.blasts);
    const wbc = parseFloat(input.wbc);
    if (isNaN(hb) || isNaN(blasts) || isNaN(wbc) || input.transfusionNeed === null) return null;
    return calculateBlast(input.sex, hb, blasts, wbc, input.transfusionNeed);
  }, [input]);

  return (
    <CalcShell
      title="BLAST — Clinical Risk Model"
      subtitle="Blood 2025 — Blasts, Leukocytes, Anemia, Sex-adjusted Thresholds"
      inputs={
        <>
          <div className="hema-input-group">
            <label className="hema-label">Sex</label>
            <div className="hema-field">
              <select value={input.sex || ''} onChange={(e) => setInput({ ...input, sex: (e.target.value || null) as typeof input.sex })}>
                <option value="">Select...</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <p className="hema-hint">Severe anemia: Hb &lt;8 (F) / &lt;9 (M), or transfusion need. Moderate: 8–&lt;10 (F) / 9–&lt;11 (M)</p>
          </div>
          <NumberField label="Hemoglobin (g/dL)" value={input.hemoglobin} onChange={(v) => setInput({ ...input, hemoglobin: v })} placeholder="e.g. 9" min={0} step={0.1} decimal />
          <YesNoToggle label="Need for red cell transfusions" value={input.transfusionNeed} onChange={(v) => setInput({ ...input, transfusionNeed: v })} hint="Counts as severe anemia regardless of Hb" />
          <NumberField label="Circulating blasts (%)" value={input.blasts} onChange={(v) => setInput({ ...input, blasts: v })} placeholder="e.g. 3" min={0} max={100} step={0.1} decimal />
          <NumberField label={<>WBC (×10⁹/L)</>} value={input.wbc} onChange={(v) => setInput({ ...input, wbc: v })} placeholder="e.g. 15" min={0} step={0.1} decimal />
        </>
      }
      aside={result ? <div className="score-display"><span className="score-label">BLAST Score</span><span className="score-value">{result.score}</span></div> : null}
    >
      <RiskScoreResult groups={blastRiskGroups} activeLevel={result?.level || null} emptyHint="Fill in all fields to calculate BLAST score" />
    </CalcShell>
  );
};

// ── BLAST-mol Calculator ──
const BlastMolCalc: React.FC = () => {
  const { state, update } = useUserInput();
  const input = state.blastMol;
  const setInput = (val: typeof input) => update('blastMol', val);

  const result = useMemo(() => {
    if (!input.sex) return null;
    const hb = parseFloat(input.hemoglobin);
    const blasts = parseFloat(input.blasts);
    const wbc = parseFloat(input.wbc);
    if (isNaN(hb) || isNaN(blasts) || isNaN(wbc) || input.transfusionNeed === null) return null;
    if (!input.molecularRisk) return null;
    return calculateBlastMol(input.sex, hb, blasts, wbc, input.transfusionNeed, input.molecularRisk as 'low' | 'intermediate' | 'high');
  }, [input]);

  return (
    <CalcShell
      title="BLAST-mol — Molecular Risk Model"
      subtitle="Blood 2025 — Clinical variables + molecular risk score"
      inputs={
        <>
          <div className="hema-input-group">
            <label className="hema-label">Sex</label>
            <div className="hema-field">
              <select value={input.sex || ''} onChange={(e) => setInput({ ...input, sex: (e.target.value || null) as typeof input.sex })}>
                <option value="">Select...</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <p className="hema-hint">Severe anemia: Hb &lt;8 (F) / &lt;9 (M), or transfusion need. Moderate: 8–&lt;10 (F) / 9–&lt;11 (M)</p>
          </div>
          <NumberField label="Hemoglobin (g/dL)" value={input.hemoglobin} onChange={(v) => setInput({ ...input, hemoglobin: v })} placeholder="e.g. 9" min={0} step={0.1} decimal />
          <YesNoToggle label="Need for red cell transfusions" value={input.transfusionNeed} onChange={(v) => setInput({ ...input, transfusionNeed: v })} hint="Counts as severe anemia regardless of Hb" />
          <NumberField label="Circulating blasts (%)" value={input.blasts} onChange={(v) => setInput({ ...input, blasts: v })} placeholder="e.g. 3" min={0} max={100} step={0.1} decimal />
          <NumberField label={<>WBC (×10⁹/L)</>} value={input.wbc} onChange={(v) => setInput({ ...input, wbc: v })} placeholder="e.g. 15" min={0} step={0.1} decimal />
          <div className="hema-input-group">
            <label className="hema-label">Molecular risk score</label>
            <div className="hema-field">
              <select value={input.molecularRisk} onChange={(e) => setInput({ ...input, molecularRisk: e.target.value })}>
                <option value="">Select...</option>
                <option value="low">Low risk (0 pts)</option>
                <option value="intermediate">Intermediate risk (1 pt)</option>
                <option value="high">High risk (2 pts)</option>
              </select>
            </div>
            <p className="hema-hint">
              Select the molecular risk level as determined by mutation analysis. The exact molecular risk scoring definition is pending verification.
            </p>
          </div>
        </>
      }
      aside={result ? <div className="score-display"><span className="score-label">BLAST-mol Score</span><span className="score-value">{result.score}</span></div> : null}
    >
      <RiskScoreResult groups={blastMolRiskGroups} activeLevel={result?.level || null} emptyHint="Fill in all fields to calculate BLAST-mol score" />
    </CalcShell>
  );
};

// ── Main component ──
const CmmlRiskScores: React.FC = () => {
  const { state, update } = useUserInput();
  const selected = state.selectedCmmlRiskModel;

  return (
    <div className="cmml-risk-scores">
      <div className="hema-input-group">
        <label className="hema-label" htmlFor="cmml-risk-model">Risk Model</label>
        <div className="hema-field">
          <select
            id="cmml-risk-model"
            value={selected}
            onChange={(e) => update('selectedCmmlRiskModel', e.target.value as CmmlRiskModelId)}
          >
            {MODELS.map((m) => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>

      {selected === 'gfm' && <GfmCalc />}
      {selected === 'mayo' && <MayoCalc />}
      {selected === 'cpss-mol' && <CpssMolCalc />}
      {selected === 'blast' && <BlastCalc />}
      {selected === 'blast-mol' && <BlastMolCalc />}
    </div>
  );
};

export default CmmlRiskScores;
